import React from 'react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { ReportColumn, STATUS_COLORS } from './types';

interface ReportTableRowProps {
  item: Record<string, any>;
  columns: ReportColumn[];
  onRowClick?: (item: Record<string, any>) => void;
  className?: string;
}

const renderCell = (item: Record<string, any>, column: ReportColumn) => {
  const cellData = item[column.key];

  switch (column.type) {
    case 'status':
      const statusConfig = cellData ? STATUS_COLORS[cellData.toLowerCase()] || STATUS_COLORS.default : STATUS_COLORS.default;
      return (
        <Badge variant="outline" className={cn('text-xs px-2 py-1 rounded-full font-medium', statusConfig.bg, statusConfig.text, statusConfig.border)}>
          {cellData}
        </Badge>
      );
    case 'currency':
      return (
        <span className="text-sm font-medium text-gray-900">
          {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cellData || 0)}
        </span>
      );
    case 'date':
      return <span className="text-sm text-gray-600">{cellData}</span>;
    case 'text':
    default:
      return <span className="text-sm text-gray-900">{cellData}</span>;
  }
};

const ReportTableRow: React.FC<ReportTableRowProps> = ({ item, columns, onRowClick, className = '' }) => {
  const handleClick = () => {
    onRowClick?.(item);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors',
        className
      )}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`View details for ${item.name || item.title}`}
    >
      <div
        className="flex-1 grid gap-4"
        style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr))` }}
      >
        {columns.map((column) => (
          <div key={column.key} className={cn('min-w-0', column.className)}>
            {renderCell(item, column)}
          </div>
        ))}
      </div>
    </div>
  );
};

ReportTableRow.displayName = 'ReportTableRow';

export { ReportTableRow };
